import { createLoadedEntity, EntityLoading, EntityNotLoaded, isEntityLoaded, isEntityNotLoaded, type Entity } from '@/Models/Entity';
import { type LoginResult } from '@/Models/LoginResult';
import { type UserModel } from '@/Models/User';
import i18next from 'i18next';
import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as API from '../../API';
import useProvidedContext from '../../Hooks/UseProvidedContext';
import type LoginData from '../../Models/LoginData';
import StartupPACSDriven from '../../Scripts/PACSDriven';

export interface AuthenticationContextData extends AuthenticationContextState, AuthenticationContextActions {}

export interface AuthenticationContextState {
    user: Entity<UserModel | null>;
}

export interface AuthenticationContextActions {
    isLoggedIn: () => boolean;
    getUserFromCookie: () => Promise<UserModel | null>;
    login: (loginData: LoginData) => Promise<LoginResult>;
    logout: () => void;
    unload: () => void;
}

const createActions = (user: Entity<UserModel | null>, setUser: (user: Entity<UserModel | null>) => void): AuthenticationContextActions => {
    const isLoggedIn = (): boolean => {
        return isEntityLoaded(user) && user.username != null;
    };
    const getUserFromCookie = async (): Promise<UserModel | null> => {
        setUser(EntityLoading.instance);
        const userModelFetchedFromCookie = await API.user.getUserDetails();

        const loadedUser = createLoadedEntity(userModelFetchedFromCookie);
        setUser(loadedUser);

        return userModelFetchedFromCookie;
    };
    const login = async (loginData: LoginData): Promise<LoginResult> => {
        const result = await API.user.login(loginData);

        if (!result.ok) {
            return {
                isSucces: false,
                code: result.status,
                statusText: result.statusText,
            };
        }

        await getUserFromCookie();
        await i18next.reloadResources();
        return {
            isSucces: true,
        };
    };
    const logout = (): void => {
        void API.user.logout()
            .then((): void => {
                if (isEntityLoaded(user)) {
                    unload();
                }
            });
    };
    const unload = (): void => setUser(createLoadedEntity(null));

    return {
        isLoggedIn,
        getUserFromCookie,
        login,
        logout,
        unload,
    };
};

export const AuthenticationContext = createContext<AuthenticationContextData | null>(null);

export function AuthenticationContextProvider({ children }: Readonly<{ children: ReactNode }>): React.ReactElement {
    const [user, setUser] = useState<Entity<UserModel | null>>(EntityNotLoaded.instance);
    const actions = createActions(user, setUser);
    const navigate = useNavigate();

    // Initial load
    useEffect(() => {
        if (!isEntityNotLoaded(user)) {
            return;
        }

        setUser(EntityLoading.instance);
        void actions.getUserFromCookie()
            .then((loadedUser) => {
                if (loadedUser) {
                    if (loadedUser.PACSDrivenURL != null && loadedUser.PACSDrivenURL !== '') {
                        StartupPACSDriven(navigate);
                    }
                }
            });
    }, [user]);

    const authContextValue = useMemo(() => ({ user, ...actions }), [user]);

    return (
        <AuthenticationContext.Provider value={authContextValue}>
            {children}
        </AuthenticationContext.Provider>
    );
}

export const useAuthenticationContext = (): AuthenticationContextData => {
    return useProvidedContext(AuthenticationContext);
};
